import { useMemo, useState } from "react";
import { useCriteria, usePhases } from "@/hooks/useLive";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import { Plus, CheckCircle2, Clock, Ban, Circle, Milestone, Shield } from "lucide-react";
import type { Criterion, CriterionStatus } from "@/lib/types";
import { cn } from "@/lib/utils";

const STATUS_META: Record<CriterionStatus, { label: string; Icon: typeof CheckCircle2; color: string; bg: string; dot: string }> = {
  not_started: { label: "Not started", Icon: Circle, color: "text-slate-600", bg: "bg-slate-100 border-slate-200", dot: "bg-slate-400" },
  in_progress: { label: "In progress", Icon: Clock, color: "text-sky-700", bg: "bg-sky-50 border-sky-200", dot: "bg-sky-500" },
  approved:    { label: "Approved", Icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", dot: "bg-emerald-500" },
  blocked:     { label: "Blocked", Icon: Ban, color: "text-rose-700", bg: "bg-rose-50 border-rose-200", dot: "bg-rose-500" },
};

const STATUSES: CriterionStatus[] = ["not_started", "in_progress", "approved", "blocked"];

function CriterionRow({ c, canEdit }: { c: Criterion; canEdit: boolean }) {
  const qc = useQueryClient();
  const [localStatus, setLocalStatus] = useState<CriterionStatus>(c.status);
  const [saving, setSaving] = useState(false);
  const meta = STATUS_META[localStatus];

  const handleStatus = async (newStatus: CriterionStatus) => {
    const prev = localStatus;
    setLocalStatus(newStatus);
    setSaving(true);
    try {
      await fetch(`/api/criteria/${c.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
        credentials: "include",
      });
      qc.invalidateQueries({ queryKey: ["criteria"] });
    } catch {
      setLocalStatus(prev);
    } finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5 transition-colors hover:bg-muted/30">
      <meta.Icon size={14} className={meta.color} />
      <div className="min-w-0 flex-1">
        <div className="text-caption font-medium text-foreground">{c.title}</div>
        {(c.owner || c.notes) && (
          <div className="mt-0.5 text-micro text-muted-foreground">
            {c.owner && <span>Owner · {c.owner}</span>}
            {c.owner && c.notes && <span> · </span>}
            {c.notes && <span className="italic">{c.notes}</span>}
          </div>
        )}
      </div>
      {canEdit ? (
        <select
          value={localStatus}
          onChange={(e) => handleStatus(e.target.value as CriterionStatus)}
          disabled={saving}
          className={cn(
            "shrink-0 rounded-full border px-2.5 py-1 text-micro font-medium focus:outline-none",
            meta.bg, meta.color, saving && "opacity-50"
          )}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
      ) : (
        <span className={cn("shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-micro font-medium", meta.bg, meta.color)}>
          <span className={cn("h-1.5 w-1.5 rounded-full", meta.dot)} /> {meta.label}
        </span>
      )}
    </div>
  );
}

function AddCriterionModal({ open, onClose, gates }: {
  open: boolean;
  onClose: () => void;
  gates: { id: string; name: string }[];
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ gateId: "", title: "", status: "not_started", owner: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.gateId) { setError("Pick a gate."); return; }
    if (!form.title.trim()) { setError("Title is required."); return; }
    setSaving(true); setError("");
    try {
      const res = await fetch("/api/criteria", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Failed" }));
        throw new Error(err.error ?? "Failed");
      }
      qc.invalidateQueries({ queryKey: ["criteria"] });
      setForm({ gateId: "", title: "", status: "not_started", owner: "", notes: "" });
      onClose();
    } catch (err) { setError(err instanceof Error ? err.message : "Failed"); }
    finally { setSaving(false); }
  };

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const input = "w-full rounded-md border border-border bg-background px-3 py-2 text-body text-foreground focus:border-primary focus:outline-none";
  const label = "mb-1 block text-caption font-semibold text-muted-foreground";

  return (
    <Modal open={open} onClose={onClose} title="Add gate criterion">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={label}>Gate <span className="text-destructive">*</span></label>
          <select value={form.gateId} onChange={(e) => set("gateId", e.target.value)} className={input}>
            <option value="">— Select a gate —</option>
            {gates.map((g) => <option key={g.id} value={g.id}>{g.name.replace(/^Gate:\s*/i, "")}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Criterion <span className="text-destructive">*</span></label>
          <input
            value={form.title} onChange={(e) => set("title", e.target.value)} className={input}
            placeholder="e.g. 70% of beta accounts hit TTV1"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>Status</label>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className={input}>
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Owner</label>
            <input value={form.owner} onChange={(e) => set("owner", e.target.value)} className={input} />
          </div>
        </div>
        <div>
          <label className={label}>Notes</label>
          <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className={input} />
        </div>
        {error && <div className="rounded-md bg-destructive/10 px-3 py-2 text-caption text-destructive">{error}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-md border border-border px-4 py-2 text-caption text-muted-foreground hover:text-foreground">Cancel</button>
          <button type="submit" disabled={saving} className="rounded-md bg-primary px-4 py-2 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-50">
            {saving ? "Saving…" : "Add criterion"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function CriteriaPanel() {
  const { isAdmin } = useAuth();
  const PHASES = usePhases();
  const CRITERIA = useCriteria();
  const [addOpen, setAddOpen] = useState(false);

  const gates = useMemo(() => PHASES.filter((p) => p.isGate), [PHASES]);

  const grouped = useMemo(() => {
    const map = new Map<string, Criterion[]>();
    gates.forEach((g) => map.set(g.id, []));
    CRITERIA.forEach((c) => {
      if (!map.has(c.gateId)) map.set(c.gateId, []);
      map.get(c.gateId)!.push(c);
    });
    return map;
  }, [CRITERIA, gates]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield size={16} className="text-primary" />
                Gate Criteria
              </CardTitle>
              <p className="mt-1 text-caption text-muted-foreground">
                One line per requirement. Admins (Stephen · Riyon · Nikhil) create criteria and set status.
                These drive gate approval in <span className="font-medium text-foreground">Gates &amp; Dates</span>.
              </p>
            </div>
            {isAdmin && (
              <button
                onClick={() => setAddOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-caption font-medium text-white hover:bg-primary/90"
              >
                <Plus size={13} /> Add criterion
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {gates.length === 0 ? (
            <p className="py-6 text-center text-caption text-muted-foreground">No gates configured.</p>
          ) : (
            gates.map((g) => {
              const items = grouped.get(g.id) ?? [];
              const done = items.filter((i) => i.status === "approved").length;
              const gateStatusPalette =
                g.status === "complete" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : g.status === "active" ? "bg-amber-50 text-amber-800 border-amber-200"
                : "bg-muted text-muted-foreground border-border";
              return (
                <div key={g.id}>
                  <div className="mb-2 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Milestone size={14} className="text-amber-500" />
                      <span className="text-body font-semibold text-foreground">{g.name.replace(/^Gate:\s*/i, "")}</span>
                    </div>
                    <span className="text-micro tabular text-muted-foreground">{g.date}</span>
                    <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-micro font-medium", gateStatusPalette)}>
                      {g.status}
                    </span>
                    {items.length > 0 && (
                      <span className="text-micro text-muted-foreground">
                        {done}/{items.length} approved
                      </span>
                    )}
                  </div>
                  {items.length === 0 ? (
                    <div className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-caption text-muted-foreground">
                      No criteria for this gate yet.{isAdmin && " Click \"Add criterion\" above."}
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {items.map((c) => <CriterionRow key={c.id} c={c} canEdit={isAdmin} />)}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <AddCriterionModal open={addOpen} onClose={() => setAddOpen(false)} gates={gates} />
    </div>
  );
}
